import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { ShieldCheck, ShieldAlert, RefreshCw, Heart, AlertTriangle, CheckCircle2, Activity, Search, Download, Wand2, Copy, ChevronRight, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export const DataIntegrityWidget = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drillOpen, setDrillOpen] = useState(false);
  const [drillData, setDrillData] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillPartner, setDrillPartner] = useState(null);
  const [drillSearch, setDrillSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [recovering, setRecovering] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/reports/data-integrity`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setData(await res.json());
    } catch {
      toast.error("Failed to load integrity data");
    } finally {
      setLoading(false);
    }
  };

  const openDrillDown = async (partner) => {
    if (!partner || partner.at_risk_count <= 0) return;
    setDrillPartner(partner);
    setDrillOpen(true);
    setDrillLoading(true);
    setDrillData(null);
    setSelectedIds(new Set());
    setDrillSearch("");
    try {
      const res = await fetch(`${API}/api/reports/data-integrity/at-risk/${encodeURIComponent(partner.client_code)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setDrillData(await res.json());
      } else {
        toast.error("Failed to load at-risk trackings");
      }
    } catch {
      toast.error("Failed to load at-risk trackings");
    } finally {
      setDrillLoading(false);
    }
  };

  const handleRecover = async (onlySelected = false) => {
    if (!drillPartner || !drillData) return;
    const ids = onlySelected ? Array.from(selectedIds) : [];
    if (onlySelected && ids.length === 0) {
      toast.error("Select at least one tracking to recover");
      return;
    }
    const total = onlySelected ? ids.length : drillData.trackings.length;
    if (!window.confirm(`Recover sell_price from PLU for ${total} tracking${total > 1 ? "s" : ""}? This will update sales.sell_price using partner ledger values.`)) {
      return;
    }
    setRecovering(true);
    try {
      const body = onlySelected ? { sale_ids: ids } : {};
      const res = await fetch(`${API}/api/reports/data-integrity/at-risk/${encodeURIComponent(drillPartner.client_code)}/recover`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const result = await res.json();
        toast.success(`Recovered ${result.recovered} tracking${result.recovered === 1 ? "" : "s"} — ₹${(result.total_amount || 0).toLocaleString()}`);
        await openDrillDown(drillPartner);
        fetchData();
      } else {
        toast.error("Recovery failed");
      }
    } catch {
      toast.error("Recovery failed");
    } finally {
      setRecovering(false);
    }
  };

  const handleExport = async () => {
    if (!drillPartner) return;
    try {
      const res = await fetch(`${API}/api/reports/data-integrity/at-risk/${encodeURIComponent(drillPartner.client_code)}/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("export failed");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `at_risk_${drillPartner.client_code}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  const copyAllTrackings = () => {
    if (!drillData?.trackings?.length) return;
    const text = drillData.trackings.map((t) => t.tracking).join("\n");
    navigator.clipboard.writeText(text).then(
      () => toast.success(`Copied ${drillData.trackings.length} tracking numbers`),
      () => toast.error("Copy failed")
    );
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  useEffect(() => { if (token) fetchData(); }, [token]);

  if (loading) {
    return (
      <section data-testid="data-integrity-widget">
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck className="w-5 h-5 text-sky-500" />
          <h2 className="text-sm lg:text-base font-semibold">Data Integrity</h2>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 lg:gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}><CardContent className="p-3 lg:p-5"><Skeleton className="h-16" /></CardContent></Card>
          ))}
        </div>
      </section>
    );
  }

  if (!data) return null;

  const score = data.health_score || 0;
  const scoreColor = score >= 90 ? "emerald" : score >= 50 ? "amber" : "rose";
  const atRisk = data.at_risk_partners || 0;

  return (
    <section data-testid="data-integrity-widget">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-sky-500" />
          <h2 className="text-sm lg:text-base font-semibold">Data Integrity</h2>
          {atRisk === 0 ? (
            <Badge variant="outline" className="text-emerald-600 border-emerald-300 bg-emerald-50 text-[10px] lg:text-xs" data-testid="integrity-status-badge">
              <CheckCircle2 className="w-3 h-3 mr-1" /> All Clear
            </Badge>
          ) : (
            <Badge variant="outline" className="text-rose-600 border-rose-300 bg-rose-50 text-[10px] lg:text-xs" data-testid="integrity-status-badge">
              <AlertTriangle className="w-3 h-3 mr-1" /> {atRisk} At Risk
            </Badge>
          )}
        </div>
        <Button variant="ghost" size="sm" onClick={fetchData} disabled={loading} className="h-7 w-7 p-0" data-testid="integrity-refresh-btn">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 lg:gap-4">
        {/* Health Score */}
        <Card className={`${score >= 90 ? "border-emerald-200 bg-gradient-to-br from-emerald-50 to-emerald-50/50 dark:from-emerald-950/20 dark:to-emerald-950/10" : score >= 50 ? "border-amber-200 bg-gradient-to-br from-amber-50 to-amber-50/50 dark:from-amber-950/20 dark:to-amber-950/10" : "border-rose-200 bg-gradient-to-br from-rose-50 to-rose-50/50 dark:from-rose-950/20 dark:to-rose-950/10"}`} data-testid="health-score-card">
          <CardContent className="p-3 lg:p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] lg:text-xs font-medium text-muted-foreground uppercase tracking-wide">Health Score</p>
                <p className={`text-2xl lg:text-3xl font-bold mt-1 ${score >= 90 ? "text-emerald-600" : score >= 50 ? "text-amber-600" : "text-rose-600"}`}>
                  {score}%
                </p>
              </div>
              <div className={`p-2 rounded-xl ${score >= 90 ? "bg-emerald-500/10" : score >= 50 ? "bg-amber-500/10" : "bg-rose-500/10"}`}>
                <Heart className={`w-5 h-5 ${score >= 90 ? "text-emerald-500" : score >= 50 ? "text-amber-500" : "text-rose-500"}`} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Partner Sales */}
        <Card className="border-sky-200 bg-gradient-to-br from-sky-50 to-blue-50/50 dark:from-sky-950/20 dark:to-blue-950/10" data-testid="partner-sales-card">
          <CardContent className="p-3 lg:p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] lg:text-xs font-medium text-muted-foreground uppercase tracking-wide">Partner Sales</p>
                <p className="text-lg lg:text-2xl font-bold mt-1 text-sky-700">
                  {data.healthy_sales?.toLocaleString()} <span className="text-xs font-normal text-muted-foreground">/ {data.total_partner_sales?.toLocaleString()}</span>
                </p>
                <p className="text-[10px] lg:text-xs text-muted-foreground mt-0.5">with sell prices</p>
              </div>
              <div className="p-2 rounded-xl bg-sky-500/10">
                <Activity className="w-5 h-5 text-sky-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recovered */}
        <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-green-50/50 dark:from-emerald-950/20 dark:to-green-950/10" data-testid="recovered-card">
          <CardContent className="p-3 lg:p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] lg:text-xs font-medium text-muted-foreground uppercase tracking-wide">Auto-Recovered</p>
                <p className="text-lg lg:text-2xl font-bold mt-1 text-emerald-700">
                  {data.recovered_count?.toLocaleString() || 0}
                </p>
                <p className="text-[10px] lg:text-xs text-muted-foreground mt-0.5">from partner uploads</p>
              </div>
              <div className="p-2 rounded-xl bg-emerald-500/10">
                <ShieldCheck className="w-5 h-5 text-emerald-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* PLU Coverage */}
        <Card className="border-violet-200 bg-gradient-to-br from-violet-50 to-purple-50/50 dark:from-violet-950/20 dark:to-purple-950/10" data-testid="plu-coverage-card">
          <CardContent className="p-3 lg:p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] lg:text-xs font-medium text-muted-foreground uppercase tracking-wide">PLU Coverage</p>
                <p className="text-lg lg:text-2xl font-bold mt-1 text-violet-700">
                  {data.partners_with_plu || 0} <span className="text-xs font-normal text-muted-foreground">partners</span>
                </p>
                <p className="text-[10px] lg:text-xs text-muted-foreground mt-0.5">uploaded ledger data</p>
              </div>
              <div className="p-2 rounded-xl bg-violet-500/10">
                <ShieldAlert className="w-5 h-5 text-violet-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Partner breakdown (only if there are partners with PLU data) */}
      {data.partner_breakdown?.length > 0 && (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {data.partner_breakdown.map((p) => {
            const isAtRisk = p.at_risk_count > 0;
            const commonProps = {
              className: `flex items-center justify-between px-3 py-2 rounded-lg border text-sm text-left transition-all ${
                p.status === "healthy"
                  ? "bg-emerald-50/50 border-emerald-200 dark:bg-emerald-950/10 dark:border-emerald-800"
                  : "bg-rose-50/50 border-rose-200 dark:bg-rose-950/10 dark:border-rose-800 cursor-pointer hover:bg-rose-100 hover:border-rose-400 hover:shadow-sm active:scale-[0.99]"
              }`,
              "data-testid": `partner-integrity-${p.client_code.toLowerCase()}`,
            };
            const inner = (
              <>
                <div className="flex items-center gap-2 min-w-0">
                  {p.status === "healthy" ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                  ) : (
                    <AlertTriangle className="w-4 h-4 text-rose-500 flex-shrink-0" />
                  )}
                  <span className="font-medium truncate">{p.name || p.client_code}</span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                  <span className="text-xs text-muted-foreground">{p.plu_trackings} trackings</span>
                  {p.at_risk_count > 0 && (
                    <Badge variant="destructive" className="text-[10px]">{p.at_risk_count} at risk</Badge>
                  )}
                  {isAtRisk && <ChevronRight className="w-3.5 h-3.5 text-rose-500" />}
                </div>
              </>
            );
            return isAtRisk ? (
              <button
                key={p.client_code}
                {...commonProps}
                type="button"
                onClick={() => openDrillDown(p)}
                title={`Click to see ${p.at_risk_count} at-risk trackings for ${p.name}`}
              >
                {inner}
              </button>
            ) : (
              <div key={p.client_code} {...commonProps}>{inner}</div>
            );
          })}
        </div>
      )}

      {/* Drill-down dialog */}
      <Dialog open={drillOpen} onOpenChange={setDrillOpen}>
        <DialogContent className="max-w-4xl w-[95vw] max-h-[90vh] flex flex-col" mobileFullScreen data-testid="at-risk-drilldown-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base lg:text-lg">
              <AlertTriangle className="w-5 h-5 text-rose-500" />
              At-Risk Trackings — {drillPartner?.name || drillPartner?.client_code}
            </DialogTitle>
            <DialogDescription className="text-xs">
              Trackings where the sales record has no sell-price but the partner&apos;s uploaded ledger (PLU) shows an amount.
            </DialogDescription>
          </DialogHeader>

          {/* Summary + actions */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 border-y py-3 -mx-6 px-6 bg-rose-50/40 dark:bg-rose-950/10">
            <div className="flex items-center gap-4 text-sm">
              <div>
                <p className="text-[10px] uppercase text-muted-foreground tracking-wide">At Risk</p>
                <p className="font-bold text-rose-600" data-testid="at-risk-count">{drillData?.count ?? "—"}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase text-muted-foreground tracking-wide">Total Recoverable</p>
                <p className="font-bold text-emerald-600" data-testid="at-risk-total">
                  ₹{(drillData?.total_recoverable || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                </p>
              </div>
              {selectedIds.size > 0 && (
                <div>
                  <p className="text-[10px] uppercase text-muted-foreground tracking-wide">Selected</p>
                  <p className="font-bold text-sky-600">{selectedIds.size}</p>
                </div>
              )}
            </div>
            <div className="sm:ml-auto flex items-center gap-2 flex-wrap">
              <Button variant="outline" size="sm" onClick={copyAllTrackings} disabled={!drillData?.trackings?.length} data-testid="at-risk-copy-btn">
                <Copy className="w-3.5 h-3.5 mr-1" /> Copy
              </Button>
              <Button variant="outline" size="sm" onClick={handleExport} disabled={!drillData?.trackings?.length} data-testid="at-risk-export-btn">
                <Download className="w-3.5 h-3.5 mr-1" /> Export
              </Button>
              {selectedIds.size > 0 ? (
                <Button size="sm" onClick={() => handleRecover(true)} disabled={recovering} className="bg-emerald-600 hover:bg-emerald-700" data-testid="at-risk-recover-selected-btn">
                  {recovering ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Wand2 className="w-3.5 h-3.5 mr-1" />}
                  Recover Selected ({selectedIds.size})
                </Button>
              ) : (
                <Button size="sm" onClick={() => handleRecover(false)} disabled={recovering || !drillData?.trackings?.length} className="bg-emerald-600 hover:bg-emerald-700" data-testid="at-risk-recover-all-btn">
                  {recovering ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Wand2 className="w-3.5 h-3.5 mr-1" />}
                  Recover All
                </Button>
              )}
            </div>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input
              placeholder="Search tracking, channel, status…"
              value={drillSearch}
              onChange={(e) => setDrillSearch(e.target.value)}
              className="pl-8 h-8 text-sm"
              data-testid="at-risk-search-input"
            />
          </div>

          {/* List */}
          <div className="flex-1 overflow-auto -mx-6 px-6">
            {drillLoading ? (
              <div className="space-y-2 py-4">
                {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
              </div>
            ) : !drillData?.trackings?.length ? (
              <div className="text-center py-10 text-sm text-muted-foreground">
                <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-500 mb-2" />
                No at-risk trackings remaining for this partner.
              </div>
            ) : (
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-background z-10 border-b">
                  <tr className="text-left">
                    <th className="py-2 pr-2 w-8">
                      <input
                        type="checkbox"
                        checked={drillData.trackings.length > 0 && selectedIds.size === drillData.trackings.length}
                        onChange={(e) => {
                          if (e.target.checked) setSelectedIds(new Set(drillData.trackings.map((t) => t.sale_id)));
                          else setSelectedIds(new Set());
                        }}
                        data-testid="at-risk-select-all"
                      />
                    </th>
                    <th className="py-2 pr-2 font-medium">Tracking</th>
                    <th className="py-2 pr-2 font-medium text-right">Recoverable</th>
                    <th className="py-2 pr-2 font-medium hidden md:table-cell">Current</th>
                    <th className="py-2 pr-2 font-medium hidden md:table-cell">Channel</th>
                    <th className="py-2 pr-2 font-medium hidden lg:table-cell">Order Date</th>
                    <th className="py-2 pr-2 font-medium">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {drillData.trackings
                    .filter((t) => {
                      if (!drillSearch.trim()) return true;
                      const q = drillSearch.toLowerCase();
                      return (
                        (t.tracking || "").toLowerCase().includes(q) ||
                        (t.channel || "").toLowerCase().includes(q) ||
                        (t.status || "").toLowerCase().includes(q)
                      );
                    })
                    .map((t) => (
                      <tr key={t.sale_id} className="border-b hover:bg-muted/40" data-testid={`at-risk-row-${t.tracking}`}>
                        <td className="py-1.5 pr-2 align-top">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(t.sale_id)}
                            onChange={() => toggleSelect(t.sale_id)}
                            data-testid={`at-risk-row-checkbox-${t.tracking}`}
                          />
                        </td>
                        <td className="py-1.5 pr-2 font-mono whitespace-nowrap">
                          <button
                            type="button"
                            className="hover:underline"
                            onClick={() => {
                              navigator.clipboard.writeText(t.tracking);
                              toast.success("Tracking copied");
                            }}
                            title="Click to copy"
                          >
                            {t.tracking}
                          </button>
                          {t.is_order_first_row && (
                            <Badge variant="outline" className="ml-1 text-[9px] py-0 px-1 border-sky-300 text-sky-600">first-row</Badge>
                          )}
                        </td>
                        <td className="py-1.5 pr-2 text-right font-semibold text-emerald-600 whitespace-nowrap">
                          ₹{Number(t.recoverable_amount || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                        </td>
                        <td className="py-1.5 pr-2 hidden md:table-cell text-rose-600">{t.current_sell_label}</td>
                        <td className="py-1.5 pr-2 hidden md:table-cell">{t.channel || "—"}</td>
                        <td className="py-1.5 pr-2 hidden lg:table-cell">{t.order_date ? String(t.order_date).slice(0, 10) : "—"}</td>
                        <td className="py-1.5 pr-2 text-muted-foreground">{t.reason}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};
